import { useState } from 'react';
import { influencer } from '@round/api';
import { Button, Empty, lucideIcons } from '@round/creatorbase-ui';
import { CreatorTableEmptyState } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/EmptyState/CreatorTableEmptyState';
import { usePoolCreators } from './hooks/usePoolCreators';
import { usePoolCreatorSelection } from './hooks/usePoolCreatorSelection';
import { CreatorsTable } from './components/CreatorsTable/CreatorsTable';
import { RemoveCreatorsModal } from './components/RemoveCreatorsModal/RemoveCreatorsModal';
import { AddCreatorsDrawer } from '../AddCreatorsDrawer/AddCreatorsDrawer';
import styles from './PoolCreators.module.css';

const { Plus, Trash2, Users } = lucideIcons;

type Props = {
    poolId: number | null | undefined;
};

const pluralizeCreators = (count: number): string => `${count} ${count === 1 ? 'creator' : 'creators'}`;

export const PoolCreators = ({ poolId }: Props) => {
    const { creators, count, isFetching, areAllPagesResolved, hasError, getCreatorImages, getAreCreatorImagesLoading } =
        usePoolCreators(poolId);
    const { selectedCreatorIds, toggleCreator, selectCreators, clearSelection } = usePoolCreatorSelection(poolId);
    const [isAddCreatorsOpen, setIsAddCreatorsOpen] = useState(false);
    // Holds the creators a remove is being confirmed for; null means the modal is closed. Both the
    // per-row action and the bulk toolbar action open the same modal with a list of one or many.
    const [creatorsToRemove, setCreatorsToRemove] = useState<influencer.CreatorSearchResult[] | null>(null);

    const selectedCreators = creators.filter((creator) => selectedCreatorIds.has(creator._id));

    // The pool loads every page before it settles, so keep the table in its loading state until they all
    // resolve rather than flashing a partial member list. An error ends the fetch without resolving them.
    const isTableLoading = isFetching && !areAllPagesResolved;

    return (
        <section className={styles.container}>
            <div className={styles.toolbar}>
                <div className={styles.heading}>
                    <h2 className={styles.title}>Creators</h2>
                    {typeof count === 'number' ? (
                        <span className={styles.countLabel}>{pluralizeCreators(count)}</span>
                    ) : null}
                </div>
                <div className={styles.headerActions}>
                    {selectedCreatorIds.size > 0 ? (
                        <>
                            <span className={styles.selectionCount}>{selectedCreatorIds.size} selected</span>
                            <Button size="sm" variant="ghost" onClick={clearSelection}>
                                Clear
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setCreatorsToRemove(selectedCreators)}>
                                <Trash2 /> Remove
                            </Button>
                        </>
                    ) : null}
                    <Button size="sm" onClick={() => setIsAddCreatorsOpen(true)}>
                        <Plus /> Add creators
                    </Button>
                </div>
            </div>

            <div className={styles.tableWrap}>
                <CreatorsTable
                    poolId={poolId}
                    rows={creators}
                    isLoading={isTableLoading}
                    hasError={hasError}
                    getCreatorImages={getCreatorImages}
                    getAreCreatorImagesLoading={getAreCreatorImagesLoading}
                    selectedCreatorIds={selectedCreatorIds}
                    onCreatorToggle={toggleCreator}
                    onSelectAll={selectCreators}
                    onClearSelection={clearSelection}
                    onRemoveCreators={setCreatorsToRemove}
                    noDataLabel={
                        hasError ? (
                            // A failed load must not present the empty-pool "Add creators" affordance.
                            <CreatorTableEmptyState hasError />
                        ) : (
                            <div className={styles.emptyState}>
                                <Empty className={styles.empty}>
                                    <Empty.Header>
                                        <Empty.Media variant="icon">
                                            <Users />
                                        </Empty.Media>
                                        <Empty.Title>No creators in this pool</Empty.Title>
                                        <Empty.Description>Add creators to start building this pool.</Empty.Description>
                                    </Empty.Header>
                                    <Empty.Content>
                                        <Button size="sm" onClick={() => setIsAddCreatorsOpen(true)}>
                                            <Plus /> Add creators
                                        </Button>
                                    </Empty.Content>
                                </Empty>
                            </div>
                        )
                    }
                />
            </div>

            <AddCreatorsDrawer isOpen={isAddCreatorsOpen} poolId={poolId} onClose={() => setIsAddCreatorsOpen(false)} />

            <RemoveCreatorsModal
                isOpen={creatorsToRemove !== null}
                poolId={poolId}
                creators={creatorsToRemove ?? []}
                onClose={() => setCreatorsToRemove(null)}
                onRemoved={clearSelection}
            />
        </section>
    );
};
